import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatHeader } from '@/components/ChatHeader';
import axios from 'axios';

const getApiBaseUrl = (): string => {
  return window.ENV?.VITE_API_BASE_URL || 'http://config.not.loaded.xxx';
};

export const WelcomePromotionPage: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');
  const [referralSource, setReferralSource] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState<string | null>(null);

  const referralOptions = [
    'Google Search',
    'Social Media',
    'Industry Conference',
    'Colleague Referral',
    'Email Newsletter',
    'Website Advertisement',
    'Other',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Get auth token if exists
      const token = sessionStorage.getItem('accessToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${getApiBaseUrl()}/api/promotions/welcome/submit`,
        {
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          mobileNumber: mobileNumber.trim(),
          company: company.trim(),
          position: position.trim(),
          referralSource: referralSource.trim(),
        },
        { headers }
      );

      // Success!
      setCouponCode(response.data.couponCode);
      setSuccess(true);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to submit welcome promotion. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToManagePlan = () => {
    navigate('/manage-plan', { state: { fromWelcomePromotion: true } });
  };

  if (success) {
    return (
      <div className="flex h-screen bg-neutral-white dark:bg-neutral-black flex-col">
        <ChatHeader showMenuButton={false} />

        <div className="flex-1 overflow-y-auto flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="bg-white dark:bg-neutral-black border border-neutral-light-gray dark:border-neutral-dark-gray rounded-lg shadow-lg p-8">
              {/* Success Icon */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full mb-4">
                  <svg
                    className="w-10 h-10 text-green-600 dark:text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold text-neutral-black dark:text-neutral-white mb-2">
                  🎉 Welcome to ChatHTL!
                </h1>
                <p className="text-lg text-neutral-medium-gray">
                  Your exclusive $10 credit is ready
                </p>
              </div>

              {/* Coupon Code Display */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-2 border-dashed border-brand-teal rounded-lg p-6 mb-6">
                <p className="text-sm text-neutral-medium-gray text-center mb-2 uppercase tracking-wide">
                  Your Coupon Code
                </p>
                <div className="text-4xl font-bold text-brand-teal text-center tracking-wider font-mono">
                  {couponCode}
                </div>
                <p className="text-xs text-neutral-medium-gray text-center mt-3">
                  This code can only be used once
                </p>
              </div>

              {/* Email Sent Notice */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                      Check your email
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      We've sent your coupon code to <strong>{email}</strong>. Don't forget to
                      check your spam/junk folder if you don't see it.
                    </p>
                  </div>
                </div>
              </div>

              {/* TFA Notice */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-purple-900 dark:text-purple-300">
                      📱 SMS Verification Required
                    </p>
                    <p className="text-xs text-purple-700 dark:text-purple-400 mt-1">
                      For security, you'll receive a text message with a verification code when
                      you redeem this coupon. Make sure your phone is nearby!
                    </p>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-neutral-black dark:text-neutral-white mb-3">
                  What's Next?
                </h2>
                <ol className="space-y-2 text-sm text-neutral-medium-gray">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-teal text-white rounded-full text-xs font-bold mr-3 flex-shrink-0">
                      1
                    </span>
                    <span>Click the button below to go to the Manage Plan page</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-teal text-white rounded-full text-xs font-bold mr-3 flex-shrink-0">
                      2
                    </span>
                    <span>Enter your coupon code in the "Redeem Coupon" section</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-teal text-white rounded-full text-xs font-bold mr-3 flex-shrink-0">
                      3
                    </span>
                    <span>Verify your identity with the SMS code sent to your phone</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-teal text-white rounded-full text-xs font-bold mr-3 flex-shrink-0">
                      4
                    </span>
                    <span>Start chatting with Ella, your AI hospitality assistant!</span>
                  </li>
                </ol>
              </div>

              {/* Action Button */}
              <div className="flex gap-4">
                <button
                  onClick={handleGoToManagePlan}
                  className="flex-1 px-6 py-3 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-md font-semibold transition-colors"
                >
                  Redeem My $10 Credit
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-neutral-light-gray dark:bg-neutral-dark-gray text-neutral-black dark:text-neutral-white rounded-md hover:bg-neutral-medium-gray dark:hover:bg-neutral-medium-gray transition-colors"
                >
                  Go to Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-neutral-white dark:bg-neutral-black flex-col">
      <ChatHeader showMenuButton={false} />

      <div className="flex-1 overflow-y-auto flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-neutral-black border border-neutral-light-gray dark:border-neutral-dark-gray rounded-lg shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-neutral-black dark:text-neutral-white mb-2">
                Welcome to ChatHTL
              </h1>
              <p className="text-lg text-neutral-medium-gray">
                Get $10 in free credits to try Ella, your AI hospitality assistant
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                  placeholder="john@example.com"
                />
              </div>

              {/* Mobile Number */}
              <div>
                <label
                  htmlFor="mobileNumber"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="mobileNumber"
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                  placeholder="+1 (555) 123-4567"
                />
                <p className="text-xs text-neutral-medium-gray mt-1">
                  Required for SMS verification when redeeming your coupon
                </p>
              </div>

              {/* Company */}
              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  Company <span className="text-red-500">*</span>
                </label>
                <input
                  id="company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                  placeholder="Acme Hotels Inc."
                />
              </div>

              {/* Position */}
              <div>
                <label
                  htmlFor="position"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  Position <span className="text-red-500">*</span>
                </label>
                <input
                  id="position"
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                  placeholder="Revenue Manager"
                />
              </div>

              {/* How did you hear about us */}
              <div>
                <label
                  htmlFor="referralSource"
                  className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
                >
                  How did you hear about us? <span className="text-red-500">*</span>
                </label>
                <select
                  id="referralSource"
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
                >
                  <option value="">Select an option</option>
                  {referralOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Claim My $10 Credit'}
                </button>
              </div>
            </form>

            {/* Terms */}
            <p className="mt-6 text-xs text-center text-neutral-medium-gray">
              By submitting this form, you agree to receive SMS verification codes and promotional
              emails. This offer is limited to one per person.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
